import _ from 'lodash'
import { InputHourSerieType } from '../../../building-energy-model/hour-series-calculator'
import { calcVentilationHourSeries } from '../../../energy-system/ventilation-hour-series-calculator'
import {
  BuildingEnergyModelFragment,
  HourSeriesType,
} from '../../../generated/graphql'
import { hoursInYear } from '../../../util/hours-in-year'
import {
  calcSpaceHeatingByEvenTempRangeChartData,
  calcSpaceHeatingByHourChartData,
  calcSumSpaceHeatingByMonthChartData,
} from './space-heating-calibration-util'
import {
  averageByEvenNumberRange,
  averageByHour,
  dayHours,
  // exportToExcel,
  hourCutSerie,
  hoursOfTheDay,
  rangeValues,
  tempCutSerie,
} from './calibration-util'
import {
  calcDhwCirculationLossChartData,
  calcDhwConsumtionByEvenTempRangeChartData,
  calcDhwConsumtionByHourChartData,
  calcSumDhwCirculationLossByMonthChartData,
  calcSumDhwConsumtionByMonthChartData,
} from './dhw-calibration-util'
import {
  calcSumVentilationHeatingByMonthChartData,
  calcVentilationHeatingByEvenTempRangeChartData,
  calcVentilationHeatingByHourChartData,
} from './ventilation-calibration-util'
import {
  createChartSerie,
  createChartSerieValues,
  createDefaultLineChartSerie,
  CustomLineStyles,
  CustomSeries,
} from './chart-util'
import { i18n } from '../../../i18n'
import { calcSpaceHeatingDensityHourSeries } from '../../../energy-system/space-heating-hour-series-calculator'
import {
  calcDhwCirculationLossDensity,
  calcDhwConsumptionDensityHourSeries,
} from '../../../energy-system/dhw-hour-series-calculator'
import { hoursInWeek } from '../../../util/hours-in-week'
import { calcNetCoolingDensityHourSerie } from '../../../energy-system/cooling-hour-series-calculator'
import { calcNetElectricityConsumtionDensityHourSerie } from '../../../energy-system/electricity-consumption-hour-series-calculator'

export const CHART_TEMP_START = -18
export const CHART_TEMP_END = 18
export const CHART_TEMP_STEP = 2

const calcNetHeatingDensitySumSerieOfRange = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
  rangeStart: number,
  rangeEnd: number,
): number[] => {
  const { area } = model

  const dhwCirculationDensity = calcDhwCirculationLossDensity(model, area)

  const dhwConsumtionDensitySerie = calcDhwConsumptionDensityHourSeries(
    model,
    year,
  ).slice(rangeStart, rangeEnd)

  const ventilationResult = calcVentilationHourSeries(
    model,
    outsideTempHourSerie,
    year,
  )

  const ventilationHeatingDemandDensitySerie =
    ventilationResult.outputs
      .find((r) => r.hourSerieType === HourSeriesType.HeatingDemand)
      ?.hourSerie.map((i) => i / area) ?? Array<number>(hoursInYear).fill(0)

  const ventilationHeatingDemandForSpaceHeatingSerie =
    ventilationResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.VentilationHeatingDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const spaceHeatingDensitySerie = calcSpaceHeatingDensityHourSeries(
    model,
    outsideTempHourSerie,
    ventilationHeatingDemandForSpaceHeatingSerie,
    area,
    year,
  )

  const sumSerie = dhwConsumtionDensitySerie.map((i, index) =>
    _.round(
      i +
        dhwCirculationDensity +
        ventilationHeatingDemandDensitySerie[index] +
        spaceHeatingDensitySerie[index],
      3,
    ),
  )

  return sumSerie
}

const calcNetHeatingOfRangeByHour = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
  rangeStart: number,
  rangeEnd: number,
  sortDescending?: boolean,
  lineType = CustomLineStyles.Solid,
  lineWidth = 2,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.netHeating')

  const hours = Array<number>(rangeEnd - rangeStart)
    .fill(0)
    .map((i, index) => index)

  const result = createDefaultLineChartSerie(
    id,
    hours,
    lineType,
    lineWidth,
    false,
  )

  let sumSerie = calcNetHeatingDensitySumSerieOfRange(
    model,
    outsideTempHourSerie,
    year,
    rangeStart,
    rangeEnd,
  )

  if (sortDescending) sumSerie = sumSerie.sort((a, b) => b - a)

  result.data = createChartSerieValues(hours, sumSerie)

  return result
}

export const calcNetHeatingInYearByHour = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries =>
  calcNetHeatingOfRangeByHour(model, outsideTempHourSerie, year, 0, hoursInYear)

export const calcNetHeatingInFirstWeekByHour = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries =>
  calcNetHeatingOfRangeByHour(
    model,
    outsideTempHourSerie,
    year,
    0,
    hoursInWeek,
    undefined,
    CustomLineStyles.Solid,
    4,
  )

export const calcSortedNetHeatingInYearByHour = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries =>
  calcNetHeatingOfRangeByHour(
    model,
    outsideTempHourSerie,
    year,
    0,
    hoursInYear,
    true,
    CustomLineStyles.Solid,
    4,
  )

export const calcSumHeatingInYearByMonth = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries[] => {
  const { area } = model

  const dhwCirculationLossDensitySerie =
    calcSumDhwCirculationLossByMonthChartData(model, year)

  const dhwConsumtionDensitySerie = calcSumDhwConsumtionByMonthChartData(
    model,
    year,
  )

  const ventilationResult = calcVentilationHourSeries(
    model,
    outsideTempHourSerie,
    year,
  )

  const ventilationHeatingDemandDensitySerie =
    ventilationResult.outputs
      .find((r) => r.hourSerieType === HourSeriesType.HeatingDemand)
      ?.hourSerie.map((i) => i / area) ?? Array<number>(hoursInYear).fill(0)

  const ventilationHeatingDensitySerie =
    calcSumVentilationHeatingByMonthChartData(
      ventilationHeatingDemandDensitySerie,
      year,
    )

  const ventilationHeatingDemandForSpaceHeatingSerie =
    ventilationResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.VentilationHeatingDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const spaceHeatingDensitySerie = calcSumSpaceHeatingByMonthChartData(
    model,
    outsideTempHourSerie,
    ventilationHeatingDemandForSpaceHeatingSerie,
    area,
    year,
  )

  return [
    dhwCirculationLossDensitySerie,
    dhwConsumtionDensitySerie,
    spaceHeatingDensitySerie,
    ventilationHeatingDensitySerie,
  ]
}

export const calcHeatingByHour = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  calScheduleSerie: number[],
  calibrationTempStart: number,
  calibrationTempEnd: number,
  year: number,
): CustomSeries[] => {
  const ventilationResult = calcVentilationHourSeries(
    model,
    outsideTempHourSerie,
    year,
  )

  const ventilationHeatingDemandForSpaceHeatingSerie =
    ventilationResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.VentilationHeatingDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const cutSerie = tempCutSerie(
    outsideTempHourSerie,
    calScheduleSerie,
    calibrationTempStart,
    calibrationTempEnd,
  )

  const dhwCirculation = calcDhwCirculationLossChartData(model)

  const dhwConsumtion = calcDhwConsumtionByHourChartData(model, cutSerie, year)

  const spaceHeating = calcSpaceHeatingByHourChartData(
    model,
    outsideTempHourSerie,
    cutSerie,
    ventilationHeatingDemandForSpaceHeatingSerie,
    year,
  )

  const ventilationHeatingDemandSerie =
    ventilationResult.outputs.find(
      (r) => r.hourSerieType === HourSeriesType.HeatingDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const ventilationData = calcVentilationHeatingByHourChartData(
    model,
    cutSerie,
    ventilationHeatingDemandSerie,
  )

  return [dhwCirculation, dhwConsumtion, spaceHeating, ventilationData]
}

export const calcHeatingByEvenTempRange = (
  model: BuildingEnergyModelFragment,
  tempRangeStart: number,
  tempRangeEnd: number,
  tempRangeStep: number,
  outsideTempHourSerie: number[],
  calScheduleSerie: number[],
  cutHourStart: number,
  cutHourEnd: number,
  year: number,
): CustomSeries[] => {
  const cutSerie = hourCutSerie(calScheduleSerie, cutHourStart, cutHourEnd)

  const dhwCirculation = calcDhwCirculationLossChartData(
    model,
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
  )

  const dhwConsumtion = calcDhwConsumtionByEvenTempRangeChartData(
    model,
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
    cutSerie,
    year,
  )

  const ventilationResult = calcVentilationHourSeries(
    model,
    outsideTempHourSerie,
    year,
  )

  const ventilationHeatingDemandForSpaceHeatingSerie =
    ventilationResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.VentilationHeatingDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const spaceHeating = calcSpaceHeatingByEvenTempRangeChartData(
    model,
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
    outsideTempHourSerie,
    cutSerie,
    ventilationHeatingDemandForSpaceHeatingSerie,
    year,
  )

  const ventilationHeatingDemandSerie =
    ventilationResult.outputs.find(
      (r) => r.hourSerieType === HourSeriesType.HeatingDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const ventilationData = calcVentilationHeatingByEvenTempRangeChartData(
    model,
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
    outsideTempHourSerie,
    cutSerie,
    ventilationHeatingDemandSerie,
  )

  return [dhwCirculation, dhwConsumtion, spaceHeating, ventilationData]
}

const calcSerieDifferenceByHour = (
  serie: number[],
  reduceSerie: number[],
  cutSerie: number[],
  hourValues: number[],
): number[] => {
  const hours = hoursOfTheDay(0)

  const serieByHour = averageByHour(hourValues, serie, cutSerie, hours)

  const reduceSerieByHour = averageByHour(
    hourValues,
    reduceSerie,
    cutSerie,
    hours,
  )

  const differenceSerie = serieByHour.map((i, index) => {
    const dataValue = reduceSerieByHour[index]
    if (i !== null && dataValue !== null) return _.round(i - dataValue, 1)
    return 0
  })

  return differenceSerie
}

const calcSerieDifferenceByHourChartData = (
  id: string,
  serie: number[],
  reduceSerie: number[],
  cutSerie: number[],
  hourValues: number[],
  lineStyle = CustomLineStyles.Solid,
  lineWidth = 4,
): CustomSeries => {
  const differenceSerie = calcSerieDifferenceByHour(
    serie,
    reduceSerie,
    cutSerie,
    hourValues,
  )

  const result = createChartSerie(
    id,
    hourValues,
    differenceSerie,
    lineStyle,
    lineWidth,
  )

  return result
}

export const calcHeatingDifferencesByHour = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  calScheduleSerie: number[],
  year: number,
): CustomSeries[] => {
  if (!model.calibrationArea) return []
  const area = model.calibrationArea

  const netHeatingDensitySerie = calcNetHeatingDensitySumSerieOfRange(
    model,
    outsideTempHourSerie,
    year,
    0,
    hoursInYear,
  )

  const correction = model.calibrationCorrection ?? 1

  const measuredDensitySerie = model.calHeatingHourSeries?.values.map(
    (v) => _.round(v / area, 5) * correction,
  )

  if (!measuredDensitySerie) return []

  const hourValues = dayHours()

  // First summercuts
  const summerCutSerie = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.summerTempStart ?? 100,
    model.calibrationLimitValues?.summerTempEnd ?? -100,
  )

  const summerSerie = calcSerieDifferenceByHourChartData(
    i18n.t('buildingEnergyModels.calibrationDialog.summerTempRange'),
    netHeatingDensitySerie,
    measuredDensitySerie,
    summerCutSerie,
    hourValues,
  )

  // Zero temp
  const zeroTempCutSerie = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.zeroWeatherTempStart ?? 100,
    model.calibrationLimitValues?.zeroWeatherTempEnd ?? -100,
  )

  const zeroTempSerie = calcSerieDifferenceByHourChartData(
    i18n.t('buildingEnergyModels.calibrationDialog.zeroTempRange'),
    netHeatingDensitySerie,
    measuredDensitySerie,
    zeroTempCutSerie,
    hourValues,
  )

  // Winter
  const winterCutSerie = tempCutSerie(
    model.calOutdoorHourSeries?.values ?? [],
    calScheduleSerie,
    model.calibrationLimitValues?.winterTempStart ?? 100,
    model.calibrationLimitValues?.winterTempEnd ?? -100,
  )

  const winterSerie = calcSerieDifferenceByHourChartData(
    i18n.t('buildingEnergyModels.calibrationDialog.winterTempRange'),
    netHeatingDensitySerie,
    measuredDensitySerie,
    winterCutSerie,
    hourValues,
  )

  return [summerSerie, zeroTempSerie, winterSerie]
}

export const calcHeatingDifferenceByEvenTempRange = (
  model: BuildingEnergyModelFragment,
  tempRangeStart: number,
  tempRangeEnd: number,
  tempRangeStep: number,
  outsideTempHourSerie: number[],
  calScheduleSerie: number[],
  year: number,
): CustomSeries => {
  const result: CustomSeries = { id: '', data: [] }

  if (!model.calibrationArea) return result
  const area = model.calibrationArea

  const netHeatingDensitySerie = calcNetHeatingDensitySumSerieOfRange(
    model,
    outsideTempHourSerie,
    year,
    0,
    hoursInYear,
  )

  const correction = model.calibrationCorrection ?? 1

  const measuredDensitySerie = model.calHeatingHourSeries?.values.map(
    (v) => _.round(v / area, 5) * correction,
  )

  if (!measuredDensitySerie) return result

  const temperatureValues = rangeValues(
    tempRangeStart,
    tempRangeEnd,
    tempRangeStep,
  )

  const heatingData = averageByEvenNumberRange(
    temperatureValues,
    outsideTempHourSerie,
    netHeatingDensitySerie,
    calScheduleSerie,
  )

  const measuredData = averageByEvenNumberRange(
    temperatureValues,
    outsideTempHourSerie,
    measuredDensitySerie,
    calScheduleSerie,
  )

  const differenceSerie = heatingData.map((i, index) => {
    const dataValue = measuredData[index]
    if (i !== null && dataValue !== null) return _.round(i - dataValue, 1)
    return 0
  })

  return createChartSerie(
    i18n.t(
      'buildingEnergyModels.calibration.fixedLegends.heatingPowerDifference',
    ),
    temperatureValues,
    differenceSerie,
    CustomLineStyles.Solid,
    4,
  )
}

export const calcSumNetEnergyOfYear = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries[] => {
  if (!model.calibrationArea) return []

  const heatingSerieXvalue = i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.heatingEnergy',
  )

  const measuredHeatingId = i18n.t(
    'buildingEnergyModels.calibration.chart.tooltips.measuredEnergy',
  )

  const measuredNetHeating = _.round(
    _.sum(model.calHeatingHourSeries?.values ?? []) /
      model.calibrationArea /
      1000,
    1,
  )

  const measuredHeating = createChartSerie(
    measuredHeatingId,
    [heatingSerieXvalue],
    [measuredNetHeating],
  )

  const heatingId = i18n.t(
    'buildingEnergyModels.calibration.chart.tooltips.calculatedEnergy',
  )

  const netHeating = _.round(
    _.sum(
      calcNetHeatingDensitySumSerieOfRange(
        model,
        outsideTempHourSerie,
        year,
        0,
        hoursInYear,
      ),
    ) / 1000,
    1,
  )

  const heating = createChartSerie(
    heatingId,
    [heatingSerieXvalue],
    [netHeating],
  )

  const heatingDifference = createChartSerie(
    i18n.t('buildingEnergyModels.calibration.chart.tooltips.differenceEnergy'),
    [heatingSerieXvalue],
    [_.round(netHeating - measuredNetHeating, 1)],
  )

  const coolingSerieXvalue = i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.coolingEnergy',
  )

  const measuredCoolingId = i18n.t(
    'buildingEnergyModels.calibration.chart.tooltips.measuredEnergy',
  )

  const measuredNetCooling = _.round(
    _.sum(model.calCoolingHourSeries?.values ?? []) /
      model.calibrationArea /
      1000,
    1,
  )

  const measuredCooling = createChartSerie(
    measuredCoolingId,
    [coolingSerieXvalue],
    [measuredNetCooling],
  )

  const coolingId = i18n.t(
    'buildingEnergyModels.calibration.chart.tooltips.calculatedEnergy',
  )

  const netCooling = _.round(
    _.sum(
      calcNetCoolingDensityHourSerie(
        model,
        outsideTempHourSerie,
        model.area,
        year,
      ),
    ) / 1000,
    1,
  )

  const cooling = createChartSerie(
    coolingId,
    [coolingSerieXvalue],
    [netCooling],
  )

  const coolingDifference = createChartSerie(
    i18n.t('buildingEnergyModels.calibration.chart.tooltips.differenceEnergy'),
    [coolingSerieXvalue],
    [_.round(netCooling - measuredNetCooling, 1)],
  )

  const electricitySerieXvalue = i18n.t(
    'buildingEnergyModels.calibration.chart.axisLegends.electricityConsumtion',
  )

  const measuredElectricityId = i18n.t(
    'buildingEnergyModels.calibration.chart.tooltips.measuredEnergy',
  )

  const measuredNetElectricity = _.round(
    _.sum(model.calElectricityHourSeries?.values ?? []) /
      model.calibrationArea /
      1000,
    1,
  )

  const measuredElectricity = createChartSerie(
    measuredElectricityId,
    [electricitySerieXvalue],
    [measuredNetElectricity],
  )

  const electricityId = i18n.t(
    'buildingEnergyModels.calibration.chart.tooltips.calculatedEnergy',
  )

  const netElectricity = _.round(
    _.sum(
      calcNetElectricityConsumtionDensityHourSerie(
        model,
        outsideTempHourSerie,
        model.area,
        year,
      ),
    ) / 1000,
    1,
  )

  const electricity = createChartSerie(
    electricityId,
    [electricitySerieXvalue],
    [netElectricity],
  )

  const electricityDifference = createChartSerie(
    i18n.t('buildingEnergyModels.calibration.chart.tooltips.differenceEnergy'),
    [electricitySerieXvalue],
    [_.round(netElectricity - measuredNetElectricity, 1)],
  )

  return [
    measuredHeating,
    heating,
    heatingDifference,
    measuredCooling,
    cooling,
    coolingDifference,
    measuredElectricity,
    electricity,
    electricityDifference,
  ]
}
